import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  FaDownload,
  FaRegCopy,
  FaRedoAlt,
  FaTruck,
  FaTimes,
} from 'react-icons/fa';
import { useAppDispatch } from '@app/store/store';
import { Order } from '../../../types/interfaces';
import { downloadBill } from '@features/download-bill';
import { MyModal } from '@shared/ui/my-modal';
import { Button } from '@shared/ui/button';
import { reverseOrder } from '@features/cancel-order';
import { RepeatOrderModal } from '@features/repeat-order';
import { UpdateSelfDeliveryInfo } from '@features/update-self-delivery-info';
import { Can } from '@shared/lib/ability';
import { copyToClipboard } from '@entities/invite/lib/invite-link';
import {
  setNotification,
  setErrorNotification,
} from '@app/store/notifications';

// Универсальный пункт меню с иконкой. Вынесен наружу, чтобы все
// действия выглядели одинаково — без него меню разъезжалось по
// высоте и иконки прыгали.
type ActionItemProps = {
  icon: React.ReactNode;
  label: string;
  onClick: (e: React.MouseEvent) => void;
  tone?: 'default' | 'danger';
};

const ActionItem = React.forwardRef<HTMLButtonElement, ActionItemProps>(
  function ActionItem({ icon, label, onClick, tone = 'default' }, ref) {
    const colorClass =
      tone === 'danger'
        ? 'text-red-500 hover:bg-red-50'
        : 'text-[#2d2c2c] hover:bg-gray-50';
    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        className={`w-full flex items-center gap-3 px-4 py-2 text-sm leading-5 ${colorClass}`}
      >
        <span className="w-4 flex justify-center text-[13px] opacity-80">
          {icon}
        </span>
        <span className="flex-1 text-left">{label}</span>
      </button>
    );
  }
);

export function OrderActions({ order }: { order: Order }) {
  const dispatch = useAppDispatch();
  const [isRepeatOrderModalOpen, setIsRepeatOrderModalOpen] = useState(false);
  const [isUpdateSelfDeliveryModalOpen, setIsUpdateSelfDeliveryModalOpen] =
    useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null
  );

  useLayoutEffect(() => {
    if (!isMenuOpen) {
      setMenuPos(null);
      return;
    }
    const reposition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      // Меню MIN-WIDTH=220px; ставим под кнопку, прижимаем правым
      // краем к правой границе триггера. `top + rect.bottom + 4` —
      // 4px зазор между кнопкой и меню.
      const MENU_MIN_WIDTH = 220;
      setMenuPos({
        top: rect.bottom + 4 + window.scrollY,
        left: rect.right - MENU_MIN_WIDTH + window.scrollX,
      });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const triggerHit = triggerRef.current?.contains(target);
      const menuHit = menuRef.current?.contains(target);
      if (!triggerHit && !menuHit) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (order.order_status?.status === 'canceled') return null;

  // Хелпер копирования: единый user-feedback через нотификации
  // (в коде уже есть и setNotification, и setErrorNotification —
  // используем их, чтобы поведение совпадало с другими «копировать»
  // в приложении, например на странице компании).
  const copyValue = async (
    value: string | null | undefined,
    okText: string,
    failText = 'Не удалось скопировать'
  ) => {
    if (!value) return;
    const ok = await copyToClipboard(value);
    dispatch(ok ? setNotification(okText) : setErrorNotification(failText));
  };

  // Считаем, какие группы действий доступны — это нужно, чтобы
  // правильно отрисовать разделитель только тогда, когда снизу
  // (деструктив) реально что-то есть.
  const hasBill = !!order?.print_form;
  const canRepeat = true; // финальная проверка идёт через <Can/> ниже
  const canUpdateSelfDelivery = order.delivery_type === 'self-delivery';
  const canCancel = !(order.paid || order.shipped);
  const hasTrack =
    !!order.delivery_track_number && order.delivery_track_number.trim() !== '';
  const hasOrderName = !!order.name && order.name.trim() !== '';

  // Если вообще нечего показывать (не должно случаться: «Повторить
  // заказ» доступно почти всегда, но защищаемся от гонок CASL) —
  // не рендерим пустую трёхточечную кнопку.
  const hasAnyAction =
    hasBill ||
    canRepeat ||
    canUpdateSelfDelivery ||
    canCancel ||
    hasTrack ||
    hasOrderName;
  if (!hasAnyAction) return null;

  // Содержимое выпадашки — выносим в переменную, чтобы рендерить
  // её через ReactDOM.createPortal на body и не зависеть от
  // overflow-родителя (см. комментарий выше).
  const dropdown = isMenuOpen && menuPos ? (
    <div
      ref={menuRef}
      className="bg-white border border-gray-200 rounded-lg shadow-lg z-[1000] min-w-[220px] py-1 font-[Montserrat]"
      style={{
        position: 'absolute',
        top: menuPos.top,
        left: menuPos.left,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* ── Документы / трекинг ── */}
      {hasBill && (
        <ActionItem
          icon={<FaDownload />}
          label="Скачать счёт"
          onClick={(e) => {
            downloadBill(e, order);
            setIsMenuOpen(false);
          }}
        />
      )}

      {hasOrderName && (
        <ActionItem
          icon={<FaRegCopy />}
          label="Скопировать номер заказа"
          onClick={(e) => {
            e.stopPropagation();
            copyValue(order.name, 'Номер заказа скопирован');
            setIsMenuOpen(false);
          }}
        />
      )}

      {hasTrack && (
        <ActionItem
          icon={<FaTruck />}
          label="Скопировать трек-номер"
          onClick={(e) => {
            e.stopPropagation();
            copyValue(order.delivery_track_number, 'Трек-номер скопирован');
            setIsMenuOpen(false);
          }}
        />
      )}

      {/* ── Действия с заказом ── */}
      {canUpdateSelfDelivery && (
        <Can I="change" a="order">
          <ActionItem
            icon={<FaTruck />}
            label="Дополнить информацию"
            onClick={(e) => {
              e.stopPropagation();
              setIsUpdateSelfDeliveryModalOpen(true);
              setIsMenuOpen(false);
            }}
          />
        </Can>
      )}

      <Can I="add" a="cartitem">
        <ActionItem
          icon={<FaRedoAlt />}
          label="Повторить заказ"
          onClick={(e) => {
            e.stopPropagation();
            setIsRepeatOrderModalOpen(true);
            setIsMenuOpen(false);
          }}
        />
      </Can>

      {/* ── Деструктив ── модалка управляется отдельным state'ом
          (isCancelOpen), чтобы пережить закрытие меню. Раньше тут
          стоял ConfirmModal, чьё состояние терялось при закрытии
          меню outside-click handler'ом — отсюда «не уходит запрос». */}
      {canCancel && (
        <>
          <div className="my-1 border-t border-gray-100" />
          <ActionItem
            icon={<FaTimes />}
            label="Отменить заказ"
            tone="danger"
            onClick={(e) => {
              e.stopPropagation();
              setIsCancelOpen(true);
              setIsMenuOpen(false);
            }}
          />
        </>
      )}
    </div>
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsMenuOpen((prev) => !prev);
        }}
        className="p-2 rounded hover:bg-gray-100 focus:outline-none"
        aria-label="Действия"
      >
        <span className="flex flex-col gap-1">
          <span className="block w-4 h-0.5 bg-gray-600" />
          <span className="block w-4 h-0.5 bg-gray-600" />
          <span className="block w-4 h-0.5 bg-gray-600" />
        </span>
      </button>

      {dropdown && ReactDOM.createPortal(dropdown, document.body)}

      {/* Cancel-модалка вне дропдауна — поэтому переживает закрытие меню. */}
      <MyModal
        isOpen={isCancelOpen}
        onClose={() => setIsCancelOpen(false)}
        size="sm"
        title="Отменить заказ"
      >
        <p className="text-sm text-[#4A4F5A] leading-relaxed">
          Вы уверены, что хотите отменить этот заказ?
        </p>
        <div className="flex items-center justify-end gap-2 mt-6">
          <Button isWhite onClick={() => setIsCancelOpen(false)}>
            Отмена
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              reverseOrder(dispatch, order);
              setIsCancelOpen(false);
            }}
          >
            Да, отменить
          </Button>
        </div>
      </MyModal>

      <RepeatOrderModal
        isOpen={isRepeatOrderModalOpen}
        onClose={() => setIsRepeatOrderModalOpen(false)}
        order={order}
      />
      <UpdateSelfDeliveryInfo
        isOpen={isUpdateSelfDeliveryModalOpen}
        onClose={() => setIsUpdateSelfDeliveryModalOpen(false)}
        order={order}
      />
    </>
  );
}
